"use client"

import React, { useState, useEffect } from "react"
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material"
import Close from "@mui/icons-material/Close"
import RefreshIcon from "@mui/icons-material/Refresh"
import Group from "@mui/icons-material/Group"
import Assignment from "@mui/icons-material/Assignment"
import Schedule from "@mui/icons-material/Schedule"
import Warning from "@mui/icons-material/Warning"
import CheckCircle from "@mui/icons-material/CheckCircle"
import Error from "@mui/icons-material/Error"
import Info from "@mui/icons-material/Info"
import Timeline from "@mui/icons-material/Timeline"
import Person from "@mui/icons-material/Person"
import ArrowBack from "@mui/icons-material/ArrowBack"
import Search from "@mui/icons-material/Search"
import AutoAwesome from "@mui/icons-material/AutoAwesome"
import axios from "axios"
import { useParams, useNavigate } from "react-router-dom"

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000'

const formatDate = (dateString) => {
  if (!dateString) return "Not specified"
  const options = { year: "numeric", month: "short", day: "numeric" }
  return new Date(dateString).toLocaleDateString("en-US", options)
}

const calculateDaysRemaining = (endDateString) => {
  if (!endDateString) return null
  const today = new Date()
  const endDate = new Date(endDateString)
  const diffTime = endDate - today
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const getStatusDisplayName = (status) => {
  if (status === null || status === undefined) return "To Do"
  const s = String(status).toLowerCase().trim()

  if (["3", "done", "completed", "closed", "finish", "finished", "15"].includes(s)) return "Done"
  if (["2", "in progress", "in-progress", "active", "working", "ongoing", "14", "12"].includes(s)) return "In Progress"
  if (["4", "review", "in review", "testing", "qa", "code review", "13"].includes(s)) return "In Review"
  if (["1", "todo", "to do", "open", "backlog", "created", "11"].includes(s)) return "To Do"

  if (!isNaN(Number(s))) {
    const num = Number(s)
    if (num >= 10 && num < 15) return "In Progress"
    if (num >= 15) return "Done"
    if (num === 2) return "In Progress"
    if (num === 3) return "Done"
    if (num === 4) return "In Review"
    return "To Do"
  }

  return status || "To Do"
}

const getPriorityDisplayName = (priority) => {
  if (priority === null || priority === undefined) return "Medium"
  const p = String(priority).toLowerCase().trim()

  if (["1", "2", "high", "highest", "critical", "urgent", "blocker", "p1", "p2", "extreme", "major"].includes(p)) return "High"
  if (["3", "medium", "normal", "moderate", "p3", "standard"].includes(p)) return "Medium"
  if (["4", "5", "low", "lowest", "minor", "trivial", "p4", "p5"].includes(p)) return "Low"

  if (!isNaN(Number(p))) {
    const num = Number(p)
    if (num <= 2) return "High"
    if (num >= 4) return "Low"
    return "Medium"
  }

  return "Medium"
}

const getSeverityDisplayName = (severity) => {
  if (severity === null || severity === undefined) return "Medium"
  const s = String(severity).toLowerCase().trim()

  if (["1", "2", "high", "highest", "blocker", "critical", "p1", "p2", "extreme", "urgent", "major"].includes(s)) return "High"
  if (["4", "5", "low", "lowest", "minor", "trivial", "p4", "p5"].includes(s)) return "Low"

  if (!isNaN(Number(s))) {
    const num = Number(s)
    if (num <= 2) return "High"
    if (num >= 4) return "Low"
    return "Medium"
  }

  return "Medium"
}

const getPriorityChipStyle = (priority) => {
  const p = getPriorityDisplayName(priority)
  if (p === "High") return { bgcolor: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA", fontWeight: 700, fontSize: "0.75rem" }
  if (p === "Low") return { bgcolor: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0", fontWeight: 700, fontSize: "0.75rem" }
  return { bgcolor: "#FFFBEB", color: "#D97706", border: "1px solid #FDE68A", fontWeight: 700, fontSize: "0.75rem" }
}

const getSeverityChipStyle = (severity) => {
  const s = getSeverityDisplayName(severity)
  if (s === "High") return { bgcolor: "#FEF2F2", color: "#EF4444", border: "1px solid #FCA5A5", fontWeight: 700, fontSize: "0.75rem" }
  if (s === "Low") return { bgcolor: "#F8FAFC", color: "#64748B", border: "1px solid #CBD5E1", fontWeight: 700, fontSize: "0.75rem" }
  return { bgcolor: "#FFFBEB", color: "#F59E0B", border: "1px solid #FCD34D", fontWeight: 700, fontSize: "0.75rem" }
}

const getAssigneeDisplayName = (issue, teamMembers = []) => {
  if (issue.assignee_name && String(issue.assignee_name).toLowerCase() !== 'unassigned') {
    return issue.assignee_name;
  }
  if (issue.assigneeName && String(issue.assigneeName).toLowerCase() !== 'unassigned') {
    return issue.assigneeName;
  }
  const rawAssignee = issue.assignee;
  if (!rawAssignee || rawAssignee === 'null' || rawAssignee === 'undefined') return 'Unassigned';

  const match = teamMembers.find(m =>
    String(m.employee_id) === String(rawAssignee) ||
    String(m.id) === String(rawAssignee) ||
    String(m.c_employee_id) === String(rawAssignee)
  );

  if (match) {
    return match.name || match.fullName || match.employee_name || `${match.firstName || ''} ${match.lastName || ''}`.trim() || rawAssignee;
  }

  return rawAssignee;
}

const getStatusColor = (status) => {
  const name = getStatusDisplayName(status)
  if (name === "Done") return "success"
  if (name === "In Progress") return "primary"
  if (name === "In Review") return "secondary"
  return "warning"
}



const ProfessionalProjectDashboard = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [employeeId, setEmployeeId] = useState(null)

  const [radarTab, setRadarTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")

  const [deepDiveOpen, setDeepDiveOpen] = useState(false)
  const [deepDiveTitle, setDeepDiveTitle] = useState("")
  const [deepDiveData, setDeepDiveData] = useState([])
  const [deepDiveType, setDeepDiveType] = useState("issues")
  const [dialogStatusFilter, setDialogStatusFilter] = useState("ALL")
  const [dialogSearch, setDialogSearch] = useState("")
  const [riskDetailsOpen, setRiskDetailsOpen] = useState(false)
  const [riskDetailsTask, setRiskDetailsTask] = useState(null)
  const [aiMitigationLoading, setAiMitigationLoading] = useState(false)
  const [aiMitigationData, setAiMitigationData] = useState(null)
  const [aiMitigationError, setAiMitigationError] = useState(null)
  const [sprints, setSprints] = useState([])
  const [statusChartFilter, setStatusChartFilter] = useState("active_all")
  const [priorityChartScope, setPriorityChartScope] = useState("all")

  const handleOpenDeepDive = (title, data, type, defaultStatus = "ALL") => {
    setDeepDiveTitle(title)
    setDeepDiveData(data || [])
    setDeepDiveType(type)
    setDialogStatusFilter(defaultStatus)
    setDialogSearch("")
    setDeepDiveOpen(true)
  }

  const handleOpenRiskDetails = async (issue) => {
    setRiskDetailsTask(issue)
    setRiskDetailsOpen(true)
    setAiMitigationLoading(true)
    setAiMitigationError(null)
    setAiMitigationData(null)

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("accessToken")
      const issueId = issue.id || issue.issue_id || issue.key

      const res = await axios.post(
        `${API_BASE_URL}/api/cpo-ppm-dashboard/issue/${issueId}/ai-mitigation`,
        { 
          customReason: issue.risk_reason || issue.reason || issue.description || "",
          taskSummary: issue.summary || issue.title || issue.taskName || "",
          assigneeName: getAssigneeDisplayName(issue, team),
          taskKey: issue.key || issue.taskId || (issue.id ? `TASK-${issue.id}` : ""),
          projectId: projectId
        },
        {
          headers: token ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` } : {},
          withCredentials: true
        }
      )

      if (res?.data?.success && res?.data?.data) {
        setAiMitigationData(res.data.data)
      }
    } catch (err) {
      console.warn("AI Mitigation fetch error in project dashboard:", err)
      setAiMitigationError(err.response?.data?.message || err.message || "Failed to generate AI recommendations")
    } finally {
      setAiMitigationLoading(false)
    }
  }

  const [dashboardData, setDashboardData] = useState({
    project: null,
    team: [],
    issues: [],
    stats: {
      totalTeamMembers: 0,
      totalIssues: 0,
      completedIssues: 0,
      overdueIssuesCount: 0,
      taskCompletionPercentage: 0,
      projectProgress: 0,
      daysRemaining: 0,
      overallHealth: "Unknown",
    },
  })

  const getEmployeeid = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return null
      const payload = JSON.parse(atob(token.split('.')[1]))
      const company_id = payload.company_id
      const response = await axios.get(`${API_BASE_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      })
      if (response.data && response.data.employee_id) {
        setEmployeeId(response.data.employee_id)
        return response.data.employee_id
      }
      return null
    } catch (err) {
      //console.error("Error fetching current user:", err)
      return null
    }
  }

  const fetchDashboardData = async () => {
    if (!projectId) {
      setError("No project ID provided")
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem("token")
      if (!token) {
        setError("Authentication token missing. Please log in.")
        setLoading(false)
        return
      }

      // Parallel execution of all required APIs in a single network request waterfall
      const [projectRes, issuesRes, teamRes, sprintsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_BASE_URL}/api/issues?project=${projectId}&project_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_BASE_URL}/api/addteam/project/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_BASE_URL}/api/sprints/project/id/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ])

      // 1. Process Project Data
      let projectData = null
      if (projectRes && projectRes.ok) {
        const projectResult = await projectRes.json()
        projectData = projectResult.project || projectResult.data || projectResult
        if (Array.isArray(projectData)) {
          projectData = projectData.find(p => String(p.id || p.project_id) === String(projectId)) || projectData[0]
        }
      }

      if (!projectData) {
        // Fallback fetch project list
        const allProjRes = await fetch(`${API_BASE_URL}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (allProjRes.ok) {
          const allResult = await allProjRes.json()
          const pList = Array.isArray(allResult) ? allResult : allResult.projects || allResult.data || []
          projectData = pList.find(p => String(p.id || p.project_id) === String(projectId))
        }
      }

      if (!projectData) {
        throw new Error("Project details could not be retrieved")
      }

      // 2. Process Issues Data
      let issuesData = []
      if (issuesRes && issuesRes.ok) {
        const issuesResult = await issuesRes.json()
        const allIssues = Array.isArray(issuesResult) ? issuesResult : issuesResult.issues || issuesResult.data || []
        const filtered = allIssues.filter((issue) =>
          String(issue.project) === String(projectId) ||
          String(issue.project_id) === String(projectId) ||
          String(issue.projectId) === String(projectId)
        )
        issuesData = filtered.length > 0 ? filtered : allIssues
      }

      // 3. Process Team Data
      let teamData = []
      if (teamRes && teamRes.ok) {
        const teamResult = await teamRes.json()
        teamData = Array.isArray(teamResult) ? teamResult : teamResult.data || teamResult.teamMembers || teamResult.members || []
      }

      // 4. Process Sprints Data
      let sprintsDataList = []
      if (sprintsRes && sprintsRes.ok) {
        const sprintResult = await sprintsRes.json()
        sprintsDataList = Array.isArray(sprintResult) ? sprintResult : sprintResult.data || []
      }

      // Secondary sprint check by project key if needed
      const projectKey = projectData.key || projectData.projectKey
      if (sprintsDataList.length === 0 && projectKey) {
        try {
          const keySprintRes = await fetch(`${API_BASE_URL}/api/sprints/project/${projectKey}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (keySprintRes.ok) {
            const keySprintResult = await keySprintRes.json()
            sprintsDataList = Array.isArray(keySprintResult) ? keySprintResult : keySprintResult.data || []
          }
        } catch (e) {
          //console.error("Sprint key fetch error:", e)
        }
      }
      setSprints(sprintsDataList)

      // Set initial status chart filter based on sprint availability
      const activeSprintsList = sprintsDataList.filter(s => String(s.status).toUpperCase() === 'ACTIVE')
      if (activeSprintsList.length > 0) {
        setStatusChartFilter("active_all")
      } else {
        setStatusChartFilter("all")
      }

      const totalTeamMembers = teamData.length
      const totalIssues = issuesData.length

      const completedIssues = issuesData.filter((issue) => {
        const status = String(issue.status || "").toLowerCase()
        return status === "completed" || status === "done" || status === "closed" || status === "3"
      }).length

      const today = new Date()
      const overdueIssuesList = issuesData
        .filter((issue) => {
          const status = String(issue.status || "").toLowerCase().trim()
          const isDone = status === "completed" || status === "done" || status === "closed" || status === "3" || status === "4"
          const dueDate = issue.dueDate || issue.endDate
          return !isDone && dueDate && new Date(dueDate) < today
        })
        .map((issue) => {
          const due = new Date(issue.dueDate || issue.endDate)
          const diffTime = Math.abs(today - due)
          const daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          return {
            ...issue,
            taskId: issue.key || (issue.id ? `TSK-${issue.id}` : ""),
            taskName: issue.summary || issue.title || "Task Item",
            dueDate: issue.dueDate || issue.endDate,
            daysOverdue: daysOverdue,
            project: projectData.key || projectData.name || "Project"
          }
        })
        .sort((a, b) => b.daysOverdue - a.daysOverdue)

      const overdueIssuesCount = overdueIssuesList.length

      const taskCompletionPercentage = totalIssues > 0 ? Math.round((completedIssues / totalIssues) * 100) : 0
      const projectProgress = projectData.completionPercentage || taskCompletionPercentage
      const daysRemaining = calculateDaysRemaining(projectData.endDate || projectData.end_date || projectData.dueDate)

      let overallHealth = "On Track"
      if (daysRemaining !== null && daysRemaining < 0 && projectProgress < 100) overallHealth = "Delayed"
      else if (overdueIssuesCount > 0) overallHealth = "Needs Attention"
      else if (projectProgress >= 90) overallHealth = "Near Completion"

      setDashboardData({
        project: projectData,
        team: teamData,
        issues: issuesData,
        overdueIssues: overdueIssuesList,
        stats: {
          totalTeamMembers,
          totalIssues,
          completedIssues,
          overdueIssuesCount,
          taskCompletionPercentage,
          projectProgress: Math.round(projectProgress),
          daysRemaining,
          overallHealth,
        },
      })
    } catch (err) {
      //console.error("Dashboard fetch error:", err)
      setError(`Failed to load project dashboard: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [projectId])

  const getFilteredChartIssues = () => {
    if (statusChartFilter === "active_all") {
      return activeSprintIssues || []
    } else if (statusChartFilter === "backlog") {
      return backlogIssues || []
    } else if (statusChartFilter === "all") {
      return issues || []
    } else {
      return (issues || []).filter(issue => String(issue.sprintId) === String(statusChartFilter))
    }
  }

  const getTaskStatusChartData = () => {
    const statusCounts = {}
    const filteredForChart = getFilteredChartIssues()

    filteredForChart.forEach(issue => {
      const label = getStatusDisplayName(issue.status)
      statusCounts[label] = (statusCounts[label] || 0) + 1
    })

    return Object.keys(statusCounts).map(label => ({
      name: label,
      value: statusCounts[label]
    }))
  }

  const handleOpenStatusIssues = (statusName = null) => {
    const scopeIssues = getFilteredChartIssues()
    let filterScopeLabel = "All Active Sprints"
    if (statusChartFilter === "active_all") filterScopeLabel = "All Active Sprints"
    else if (statusChartFilter === "backlog") filterScopeLabel = "Product Backlog"
    else if (statusChartFilter === "all") filterScopeLabel = "All Project Issues"
    else {
      const sp = sprints.find(s => String(s.id) === String(statusChartFilter))
      filterScopeLabel = sp ? sp.name : "Sprint"
    }

    const title = statusName 
      ? `Task Status: ${statusName} (${filterScopeLabel})`
      : `Task Status Breakdown (${filterScopeLabel})`

    handleOpenDeepDive(title, scopeIssues, "issues", statusName || "ALL")
  }

  const getPriorityChartIssues = () => {
    if (priorityChartScope === "active") {
      return activeSprintIssues || []
    } else if (priorityChartScope === "backlog") {
      return backlogIssues || []
    } else {
      return issues || []
    }
  }

  const getWorkItemMetricsData = () => {
    const targetIssues = getPriorityChartIssues()

    const priorityCounts = { High: 0, Medium: 0, Low: 0 }
    const severityCounts = { High: 0, Medium: 0, Low: 0 }

    targetIssues.forEach(issue => {
      const p = getPriorityDisplayName(issue.priority)
      priorityCounts[p] = (priorityCounts[p] || 0) + 1

      const s = getSeverityDisplayName(issue.severity)
      severityCounts[s] = (severityCounts[s] || 0) + 1
    })

    return [
      { name: "High", Priority: priorityCounts.High, Severity: severityCounts.High },
      { name: "Medium", Priority: priorityCounts.Medium, Severity: severityCounts.Medium },
      { name: "Low", Priority: priorityCounts.Low, Severity: severityCounts.Low }
    ]
  }

  const handleOpenPrioritySeverityIssues = (metricType = null, level = null) => {
    const scopeIssues = getPriorityChartIssues()
    let scopeLabel = "All Project"
    if (priorityChartScope === "active") scopeLabel = "Active Sprint"
    else if (priorityChartScope === "backlog") scopeLabel = "Product Backlog"

    let filterKey = "ALL"
    let title = `Priority & Severity Breakdown (${scopeLabel})`

    if (metricType && level) {
      filterKey = `${metricType}: ${level}`
      title = `${metricType} - ${level} Tasks (${scopeLabel})`
    } else if (level) {
      filterKey = level
      title = `${level} Priority & Severity Tasks (${scopeLabel})`
    }

    handleOpenDeepDive(title, scopeIssues, "priority_severity", filterKey)
  }

  const CHART_COLORS = ["#3B82F6", "#F59E0B", "#10B981", "#8B5CF6"]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: "#F8FAFC" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} thickness={4} sx={{ color: "#3B82F6" }} />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Loading Project Details...
          </Typography>
        </Stack>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} action={<Button color="inherit" size="small" onClick={fetchDashboardData}>Retry</Button>}>
          {error}
        </Alert>
       
      </Box>
    )
  }

  const { project, team, issues, stats } = dashboardData

  const activeSprints = sprints.filter(s => String(s.status).toUpperCase() === 'ACTIVE')
  const activeSprintIds = activeSprints.map(s => s.id)

  const activeSprintIssues = issues.filter(issue => 
    issue.sprintId !== null && 
    issue.sprintId !== undefined && 
    activeSprintIds.includes(issue.sprintId)
  )

  const futureSprintIds = sprints.filter(s => String(s.status).toUpperCase() === 'FUTURE').map(s => s.id)

  const backlogIssues = issues.filter(issue => 
    issue.sprintId !== null && 
    issue.sprintId !== undefined && 
    futureSprintIds.includes(issue.sprintId)
  )

  const completedActiveSprintIssuesCount = activeSprintIssues.filter((issue) => {
    const status = String(issue.status || "").toLowerCase()
    return status === "completed" || status === "done" || status === "closed" || status === "3"
  }).length

  const activeSprintCompletionPct = activeSprintIssues.length > 0 
    ? Math.round((completedActiveSprintIssuesCount / activeSprintIssues.length) * 100) 
    : 0

  // Sprint status breakdown and statistics
  const totalSprintsCount = sprints.length
  const completedSprintsCount = sprints.filter(s => String(s.status).toUpperCase() === 'COMPLETED').length
  const activeSprintsCount = sprints.filter(s => String(s.status).toUpperCase() === 'ACTIVE').length
  const futureSprintsCount = sprints.filter(s => String(s.status).toUpperCase() === 'FUTURE').length
  const currentActiveSprint = sprints.find(s => String(s.status).toUpperCase() === 'ACTIVE')
  const sprintsCompletionPct = totalSprintsCount > 0 ? Math.round((completedSprintsCount / totalSprintsCount) * 100) : 0

  const taskStatusData = getTaskStatusChartData()
  const workItemMetricsData = getWorkItemMetricsData()

  const filteredIssues = issues.filter((issue) => {
    const summary = String(issue.summary || issue.title || "").toLowerCase()
    const key = String(issue.key || "").toLowerCase()
    const assignee = String(getAssigneeDisplayName(issue, team)).toLowerCase()
    const query = searchQuery.toLowerCase()

    const matchesSearch = !query || summary.includes(query) || key.includes(query) || assignee.includes(query)
    const statusName = getStatusDisplayName(issue.status)
    const matchesStatus = statusFilter === "ALL" || statusName === statusFilter

    return matchesSearch && matchesStatus
  })

  return (
    <Box sx={{ minHeight: "100vh",  py: 3,  }}>
      <Container maxWidth="xl">
        {/* Header Navigation & Banner */}
        <Box sx={{ mb: 3 }}>
         

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid #E2E8F0",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: "#0F172A" }}>
                    {project?.name || "Project Dashboard"}
                  </Typography>
                  {(project?.key || project?.projectKey) && (
                    <Chip label={project.key || project.projectKey} size="small" variant="outlined" sx={{ fontWeight: 600, height: 24 }} />
                  )}
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1}>
                  <Chip
                    icon={<Person sx={{ fontSize: 16 }} />}
                    label={`PO: ${project?.product_owner_name || project?.lead_name || project?.lead?.name || 'Not assigned'}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontWeight: 600, height: 26 }}
                  />
                  <Chip
                    icon={<Person sx={{ fontSize: 16 }} />}
                    label={`PM: ${project?.product_manager_name || project?.manager_name || 'Not assigned'}`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ fontWeight: 600, height: 26 }}
                  />
                  <Chip
                    label={stats.overallHealth}
                    size="small"
                    color={stats.overallHealth === "Overdue" ? "error" : "success"}
                    sx={{ fontWeight: 600, height: 26 }}
                  />
                  {stats.daysRemaining !== null && (
                    <Chip
                      icon={<Schedule sx={{ fontSize: 16 }} />}
                      label={stats.daysRemaining > 0 ? `${stats.daysRemaining} days left` : stats.daysRemaining < 0 ? "Overdue" : "Due today"}
                      size="small"
                      color={stats.daysRemaining > 0 ? "default" : "error"}
                      variant="outlined"
                      sx={{ fontWeight: 600, height: 26 }}
                    />
                  )}
                </Stack>
              </Box>

              <Tooltip title="Refresh Project Data">
                <IconButton onClick={fetchDashboardData} size="small" sx={{ bgcolor: "#F8FAFC", border: "1px solid #CBD5E1" }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        </Box>

        {/* 5 EXECUTIVE KPI CARDS (Uniform Height & Alignment) */}
        <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
          {/* Card 1: Project Progress */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Tooltip title="Click to view Project Roadmap" arrow>
              <Card
                elevation={0}
                onClick={() => navigate(`/project/${projectId}/roadmap`)}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  border: "1px solid #E2E8F0",
                  height: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)", borderColor: "#3B82F6" }
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase" }}>
                      Project Progress 
                    </Typography>
                    <Avatar sx={{ bgcolor: "#EFF6FF", color: "#3B82F6", width: 40, height: 40, borderRadius: 2 }}>
                      <Timeline fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A" }}>
                    {stats.projectProgress}%
                  </Typography>
                </Box>

                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress variant="determinate" value={stats.projectProgress} sx={{ height: 6, borderRadius: 3, bgcolor: "#F1F5F9" }} />
                </Box>
              </Card>
            </Tooltip>
          </Grid>

       <Grid item xs={12} sm={6} md={2.4}>
            <Tooltip title={`Completed: ${completedSprintsCount} | Active: ${activeSprintsCount} | Future: ${futureSprintsCount}`} arrow>
              <Card
                elevation={0}
                onClick={() => navigate(`/project/${projectId}/backlog/sprint/active`)}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  border: "1px solid #E2E8F0",
                  height: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)", borderColor: "#10B981" }
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase" }}>
                      Project Sprints 
                    </Typography>
                    <Avatar sx={{ bgcolor: "#ECFDF5", color: "#10B981", width: 40, height: 40, borderRadius: 2 }}>
                      <Assignment fontSize="small" />
                    </Avatar>
                  </Stack>
                  <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A" }}>
                    {completedSprintsCount + activeSprintsCount} / {totalSprintsCount}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#475569", fontWeight: 500, display: "block", mt: 0.5 }}>
                    {currentActiveSprint ? `Active: ${currentActiveSprint.name}` : 'No Active Sprint'}
                  </Typography>
                </Box>

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600, display: "block" }}>
                    ✓ {sprintsCompletionPct}% Sprints Done
                  </Typography>
                </Box>
              </Card>
            </Tooltip>
          </Grid>

          {/* Card 3: Product Backlog */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              elevation={0}
              onClick={() => handleOpenDeepDive("Product Backlog Task Inspection", backlogIssues, "backlog_issues")}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                height: "90%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)", borderColor: "#F59E0B" }
              }}
            >
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase" }}>
                    Product Backlog 
                  </Typography>
                  <Avatar sx={{ bgcolor: "#FFFBEB", color: "#F59E0B", width: 40, height: 40, borderRadius: 2 }}>
                    <Assignment fontSize="small" />
                  </Avatar>
                </Stack>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A" }}>
                  {backlogIssues.length} Tasks
                </Typography>
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: "#F59E0B", fontWeight: 600, display: "block" }}>
                  📋 Unallocated Issues
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Card 4: Team Allocation */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              elevation={0}
              onClick={() => handleOpenDeepDive("Team Members & Roles", team, "team")}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                height: "90%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)", borderColor: "#8B5CF6" }
              }}
            >
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase" }}>
                    Team Members 
                  </Typography>
                  <Avatar sx={{ bgcolor: "#F5F3FF", color: "#8B5CF6", width: 40, height: 40, borderRadius: 2 }}>
                    <Group fontSize="small" />
                  </Avatar>
                </Stack>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#0F172A" }}>
                  {stats.totalTeamMembers}
                </Typography>
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: "#8B5CF6", fontWeight: 600, display: "block" }}>
                  👥 Assigned Members
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Card 5: Overdue Tasks */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                height: "90%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)", borderColor: "#EF4444" }
              }}
            >
              <Box onClick={() => {
                const today = new Date()
                const realOverdue = issues
                  .filter(i => {
                    const status = String(i.status || "").toLowerCase().trim()
                    const isDone = status === "completed" || status === "done" || status === "closed" || status === "3" || status === "4"
                    const dueDate = i.dueDate || i.endDate
                    return !isDone && dueDate && new Date(dueDate) < today
                  })
                  .map(i => {
                    const due = new Date(i.dueDate || i.endDate)
                    const diffTime = Math.abs(today - due)
                    const daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                    return {
                      ...i,
                      taskId: i.key || (i.id ? `TSK-${i.id}` : ""),
                      taskName: i.summary || i.title || "Task Item",
                      dueDate: i.dueDate || i.endDate,
                      daysOverdue: daysOverdue,
                      project: project?.key || project?.name || "Project"
                    }
                  })
                  .sort((a, b) => b.daysOverdue - a.daysOverdue)
                handleOpenDeepDive("Top Overdue Tasks", realOverdue, "overdue_tasks")
              }} sx={{ cursor: "pointer" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="caption" fontWeight={600} sx={{ color: "#64748B", textTransform: "uppercase" }}>
                    Overdue Tasks 
                  </Typography>
                  <Avatar sx={{ bgcolor: "#FEF2F2", color: "#EF4444", width: 40, height: 40, borderRadius: 2 }}>
                    <Warning fontSize="small" />
                  </Avatar>
                </Stack>
                <Typography variant="h5" fontWeight={700} sx={{ color: (stats.overdueIssuesCount || 0) > 0 ? "#EF4444" : "#0F172A" }}>
                  {stats.overdueIssuesCount || 0}
                </Typography>
              </Box>


              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: (stats.overdueIssuesCount || 0) > 0 ? "#EF4444" : "#10B981", fontWeight: 600, display: "block" }}>
                  {(stats.overdueIssuesCount || 0) > 0 ? "🚨 Needs Attention" : "✓ No Overdue Tasks"}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* DUAL CHARTS SECTION */}
        <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
          {/* Chart 1: Task Execution Status Distribution */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box 
                    onClick={() => handleOpenStatusIssues()}
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1, 
                      cursor: "pointer",
                      p: 0.5,
                      borderRadius: 1.5,
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        bgcolor: "#F1F5F9",
                        "& .status-title": { color: "#2563EB" },
                        "& .status-chip": { bgcolor: "#2563EB", color: "#ffffff" }
                      } 
                    }}
                  >
                    <Typography className="status-title" variant="subtitle1" fontWeight={700} sx={{ color: "#0F172A", transition: "color 0.2s" }}>
                      Task Status Breakdown
                    </Typography>
                    <Tooltip title="Click to view tasks in dialog" arrow>
                      <Chip 
                        className="status-chip"
                        label="View Issues" 
                        size="small" 
                        sx={{ 
                          height: 22, 
                          fontSize: "0.7rem", 
                          fontWeight: 700, 
                          cursor: "pointer", 
                          bgcolor: "#EFF6FF", 
                          color: "#2563EB", 
                          border: "1px solid #BFDBFE",
                          transition: "all 0.2s"
                        }} 
                      />
                    </Tooltip>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={statusChartFilter}
                      onChange={(e) => setStatusChartFilter(e.target.value)}
                      sx={{ fontSize: "0.75rem", height: 28 }}
                    >
                      {activeSprints.length > 0 && (
                        <MenuItem value="active_all" sx={{ fontSize: "0.75rem" }}>All Active Sprints</MenuItem>
                      )}
                      {activeSprints.map(s => (
                        <MenuItem key={s.id} value={String(s.id)} sx={{ fontSize: "0.75rem" }}>🏃‍♂️ {s.name}</MenuItem>
                      ))}
                      <MenuItem value="backlog" sx={{ fontSize: "0.75rem" }}>📋 Product Backlog</MenuItem>
                      <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>📁 All Project Issues</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary" mb={2} display="block">
                  Distribution of tasks across workflow stages • Click slice, status, or title to inspect issues
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {taskStatusData.length > 0 ? (
                  <Box sx={{ height: 260, width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={taskStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={4}
                          dataKey="value"
                          cursor="pointer"
                          onClick={(entry) => {
                            if (entry && entry.name) {
                              handleOpenStatusIssues(entry.name)
                            }
                          }}
                        >
                          {taskStatusData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={
                                entry.name === "Done" ? "#10B981" :
                                entry.name === "In Progress" ? "#3B82F6" :
                                entry.name === "In Review" ? "#8B5CF6" :
                                entry.name === "To Do" ? "#F59E0B" :
                                CHART_COLORS[index % CHART_COLORS.length]
                              } 
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend 
                          verticalAlign="bottom" 
                          height={32} 
                          wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }}
                          onClick={(e) => {
                            if (e && e.value) {
                              handleOpenStatusIssues(e.value)
                            }
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No tasks created yet</Typography>
                  </Box>
                )}
              </Box>

              {/* Quick Status Chips */}
              {taskStatusData.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center" mt={1.5}>
                  {taskStatusData.map((item) => {
                    const color = 
                      item.name === "Done" ? "#10B981" :
                      item.name === "In Progress" ? "#3B82F6" :
                      item.name === "In Review" ? "#8B5CF6" :
                      item.name === "To Do" ? "#F59E0B" : "#64748B";
                    return (
                      <Tooltip key={item.name} title={`Click to view ${item.name} issues in dialog`} arrow>
                        <Chip
                          label={`${item.name}: ${item.value}`}
                          size="small"
                          onClick={() => handleOpenStatusIssues(item.name)}
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            bgcolor: "#F8FAFC",
                            border: `1.5px solid ${color}33`,
                            color: "#1E293B",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: `${color}15`,
                              borderColor: color,
                              color: color,
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                            }
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
            </Card>
          </Grid>

          {/* Chart 2: Task Priority & Severity Distribution */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #E2E8F0",
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Box 
                    onClick={() => handleOpenPrioritySeverityIssues()}
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1, 
                      cursor: "pointer",
                      p: 0.5,
                      borderRadius: 1.5,
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        bgcolor: "#F1F5F9",
                        "& .priority-title": { color: "#2563EB" },
                        "& .priority-chip": { bgcolor: "#2563EB", color: "#ffffff" }
                      } 
                    }}
                  >
                    <Typography className="priority-title" variant="subtitle1" fontWeight={700} sx={{ color: "#0F172A", transition: "color 0.2s" }}>
                      Work Item Priority & Severity Breakdown
                    </Typography>
                    <Tooltip title="Click to view work items in dialog" arrow>
                      <Chip 
                        className="priority-chip"
                        label="View Issues" 
                        size="small" 
                        sx={{ 
                          height: 22, 
                          fontSize: "0.7rem", 
                          fontWeight: 700, 
                          cursor: "pointer", 
                          bgcolor: "#EFF6FF", 
                          color: "#2563EB", 
                          border: "1px solid #BFDBFE",
                          transition: "all 0.2s"
                        }} 
                      />
                    </Tooltip>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={priorityChartScope}
                      onChange={(e) => setPriorityChartScope(e.target.value)}
                      sx={{ fontSize: "0.75rem", fontWeight: 600, borderRadius: 2, height: 32 }}
                    >
                      <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>All Project</MenuItem>
                      <MenuItem value="active" sx={{ fontSize: "0.75rem" }}>Active Sprint</MenuItem>
                      <MenuItem value="backlog" sx={{ fontSize: "0.75rem" }}>Product Backlog</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary" mb={2} display="block">
                  Priority vs Severity distribution based on selection • Click any bar to inspect issues
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ height: 260, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={workItemMetricsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                      <XAxis 
                        dataKey="name" 
                        stroke="#64748B" 
                        fontSize={11} 
                        cursor="pointer"
                        onClick={(e) => {
                          if (e && e.value) {
                            handleOpenPrioritySeverityIssues(null, e.value)
                          }
                        }}
                      />
                      <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '11px' }} />
                      <Bar 
                        dataKey="Priority" 
                        fill="#3B82F6" 
                        radius={[4, 4, 0, 0]} 
                        barSize={16} 
                        cursor="pointer"
                        onClick={(entry) => {
                          const lvl = entry?.name || entry?.payload?.name;
                          if (lvl) handleOpenPrioritySeverityIssues('Priority', lvl);
                        }}
                      />
                      <Bar 
                        dataKey="Severity" 
                        fill="#EF4444" 
                        radius={[4, 4, 0, 0]} 
                        barSize={16} 
                        cursor="pointer"
                        onClick={(entry) => {
                          const lvl = entry?.name || entry?.payload?.name;
                          if (lvl) handleOpenPrioritySeverityIssues('Severity', lvl);
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Interactive Priority & Severity Badges */}
              <Box display="flex" flexWrap="wrap" gap={0.8} justifyContent="center" mt={1.5}>
                {["High", "Medium", "Low"].map((level) => {
                  const item = workItemMetricsData.find(d => d.name === level) || { Priority: 0, Severity: 0 };
                  return (
                    <React.Fragment key={level}>
                      <Tooltip title={`Click to view ${level} Priority tasks in dialog`} arrow>
                        <Chip
                          label={`${level} Pri: ${item.Priority}`}
                          size="small"
                          onClick={() => handleOpenPrioritySeverityIssues('Priority', level)}
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            bgcolor: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            color: "#1D4ED8",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#DBEAFE",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                            }
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={`Click to view ${level} Severity tasks in dialog`} arrow>
                        <Chip
                          label={`${level} Sev: ${item.Severity}`}
                          size="small"
                          onClick={() => handleOpenPrioritySeverityIssues('Severity', level)}
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            bgcolor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            color: "#DC2626",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#FEE2E2",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                            }
                          }}
                        />
                      </Tooltip>
                    </React.Fragment>
                  );
                })}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* FULL WIDTH TASK EXECUTION & PROJECT OVERVIEW HUB */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#ffffff",
            border: "1px solid #E2E8F0",
            p: 2.5,
            mb: 3
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                       <Tabs 
              value={radarTab} 
              onChange={(e, newValue) => setRadarTab(newValue)} 
              textColor="primary" 
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label={`Project Tasks Radar (${issues.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
              <Tab label={`Team Workload Matrix (${team.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
              <Tab label="Project Overview & Details" sx={{ textTransform: "none", fontWeight: 700 }} />
            </Tabs>

          </Box>

          {/* TAB 0: TASK EXECUTION RADAR (SEARCH + STATUS FILTER + TABLE) */}
          {radarTab === 0 && (
            <Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="center" mb={2}>
                <TextField
                  placeholder="Search by task summary, key, or assignee..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: { xs: "100%", sm: 360 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: "#64748B" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Filter Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Filter Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    <MenuItem value="To Do">To Do</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="In Review">In Review</MenuItem>
                    <MenuItem value="Done">Done</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: "1px solid #F1F5F9" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                    <TableRow>
                      <TableCell><strong>WHO (ASSIGNEE)</strong></TableCell>
                      <TableCell><strong>WHAT (TASK KEY & SUMMARY)</strong></TableCell>
                      <TableCell><strong>DUE DATE</strong></TableCell>
                      <TableCell><strong>STATUS</strong></TableCell>
                      <TableCell><strong>PRIORITY</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIssues.length > 0 ? (
                      filteredIssues.map((issue) => (
                        <TableRow key={issue.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {getAssigneeDisplayName(issue, team)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>{issue.summary || issue.title}</Typography>
                            <Chip label={issue.key || `TASK-${issue.id}`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(issue.dueDate || issue.endDate)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={getStatusDisplayName(issue.status)} size="small" color={getStatusColor(issue.status)} sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={getPriorityDisplayName(issue.priority)} size="small" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary" py={3}>No tasks matching search filters.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* TAB 1: TEAM WORKLOAD MATRIX */}
          {radarTab === 1 && (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell><strong>DEVELOPER NAME</strong></TableCell>
                    <TableCell><strong>ROLE</strong></TableCell>
                    <TableCell><strong>ASSIGNED TASKS</strong></TableCell>
                    <TableCell><strong>COMPLETED WORKLOAD</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {team.length > 0 ? (
                    team.map((member, idx) => {
                      const memberName = member.name || member.fullName || member.employee_name || 'Developer';
                      const assignedTasks = issues.filter(i => getAssigneeDisplayName(i, team).toLowerCase() === memberName.toLowerCase());
                      const completedCount = assignedTasks.filter(i => getStatusDisplayName(i.status) === 'Done').length;
                      const progressRatio = assignedTasks.length > 0 ? Math.round((completedCount / assignedTasks.length) * 100) : 0;

                      return (
                        <TableRow 
                          key={idx} 
                          hover
                          onClick={() => handleOpenDeepDive(`${memberName}'s Assigned Tasks`, assignedTasks, "issues")}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>{memberName}</Typography>
                            <Typography variant="caption" color="text.secondary">{member.email || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={member.role || 'Developer'} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{assignedTasks.length} Tasks Assigned</Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                              <Typography variant="caption" color="text.secondary">{completedCount} / {assignedTasks.length} Done</Typography>
                              <Typography variant="caption" fontWeight={700}>{progressRatio}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={progressRatio} sx={{ height: 6, borderRadius: 3 }} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No team members assigned.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* TAB 2: PROJECT OVERVIEW & DETAILS */}
          {radarTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" fontWeight={700} mb={1}>
                    Project Key Details
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body2" fontWeight={500}>{project?.description || "No project description provided."}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Template & Category</Typography>
                      <Typography variant="body2" fontWeight={600}>{project?.template || project?.projectType || "Software Development"}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created Date</Typography>
                      <Typography variant="body2">{formatDate(project?.created_at || project?.startDate)}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" fontWeight={700} mb={1}>
                    Delivery Timeline & Leadership
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Start Date ➔ Target Delivery</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(project?.startDate || project?.start_date)} ➔ {formatDate(project?.endDate || project?.end_date)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Product Owner (PO)</Typography>
                      <Typography variant="body2" fontWeight={600}>{project?.product_owner_name || project?.lead_name || project?.lead?.name || 'Not assigned'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Product Manager (PM)</Typography>
                      <Typography variant="body2" fontWeight={600}>{project?.product_manager_name || project?.manager_name || 'Not assigned'}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Card>

        {/* Interactive Deep-Dive Inspection Modal */}
        <Dialog
          open={deepDiveOpen}
          onClose={() => setDeepDiveOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F8FAFC', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {deepDiveTitle}
              </Typography>
            </Box>
            <IconButton onClick={() => setDeepDiveOpen(false)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {deepDiveType === 'active_sprint_issues' && (
              <Box display="flex" flexDirection="column" gap={3}>
                {activeSprints.map((sprint) => {
                  const sprintIssues = activeSprintIssues.filter(i => i.sprintId === sprint.id)
                  return (
                    <Paper key={sprint.id} variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: "#f8fafc" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">
                          🏃‍♂️ {sprint.name}
                        </Typography>
                        <Chip
                          label={sprint.endDate ? `Ends: ${new Date(sprint.endDate).toLocaleDateString()}` : 'No end date'}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                            <TableRow>
                              <TableCell><strong>ASSIGNEE</strong></TableCell>
                              <TableCell><strong>TASK KEY & SUMMARY</strong></TableCell>
                              <TableCell><strong>DUE DATE</strong></TableCell>
                              <TableCell><strong>STATUS</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sprintIssues.length > 0 ? (
                              sprintIssues.map((issue) => (
                                <TableRow key={issue.id} hover>
                                  <TableCell>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      {getAssigneeDisplayName(issue, team)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="subtitle2" fontWeight={600}>{issue.summary || issue.title}</Typography>
                                    <Chip label={issue.key || `TASK-${issue.id}`} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{formatDate(issue.dueDate || issue.endDate)}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={getStatusDisplayName(issue.status)} size="small" color={getStatusColor(issue.status)} />
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} align="center">No tasks in this sprint.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )
                })}
                {activeSprints.length === 0 && (
                  <Typography align="center" color="text.secondary">No active sprints found.</Typography>
                )}
              </Box>
            )}

            {deepDiveType === 'backlog_issues' && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                    <TableRow>
                      <TableCell><strong>ASSIGNEE</strong></TableCell>
                      <TableCell><strong>TASK KEY & SUMMARY</strong></TableCell>
                      <TableCell><strong>DUE DATE</strong></TableCell>
                      <TableCell><strong>STATUS</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backlogIssues.length > 0 ? (
                      backlogIssues.map((issue) => (
                        <TableRow key={issue.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {getAssigneeDisplayName(issue, team)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>{issue.summary || issue.title}</Typography>
                            <Chip label={issue.key || `TASK-${issue.id}`} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(issue.dueDate || issue.endDate)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={getStatusDisplayName(issue.status)} size="small" color={getStatusColor(issue.status)} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No tasks in the backlog.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {deepDiveType === 'overdue_tasks' && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b", py: 1.5 }}>Task ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Task Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Assignee</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Due Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#64748b" }}>Overdue Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deepDiveData && deepDiveData.length > 0 ? (
                      deepDiveData.map((task, idx) => (
                        <TableRow
                          key={task.id || idx}
                          hover
                          onClick={() => handleOpenRiskDetails(task)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#f8fafc" },
                            "&:last-child td, &:last-child th": { border: 0 }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: "#334155", fontSize: "0.85rem" }}>
                            {task.taskId || task.key || (task.id ? `TSK-${task.id}` : "")}
                          </TableCell>
                          <TableCell sx={{ color: "#0f172a", fontWeight: 500, fontSize: "0.85rem", maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {task.taskName || task.summary || task.title}
                          </TableCell>
                          <TableCell sx={{ color: "#334155", fontSize: "0.85rem" }}>
                            {getAssigneeDisplayName(task, team)}
                          </TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                            {task.project || project?.key || project?.name}
                          </TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                            {formatDate(task.dueDate || task.endDate)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" sx={{ fontWeight: 700, color: (task.daysOverdue || 0) > 0 ? "#dc2626" : "#64748b", fontSize: "0.85rem" }}>
                              {task.daysOverdue}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#64748b" }}>
                          No Overdue Tasks
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {deepDiveType === 'issues' && (() => {
              const filteredDialogIssues = (deepDiveData || []).filter((issue) => {
                const matchesStatus =
                  dialogStatusFilter === "ALL" ||
                  getStatusDisplayName(issue.status).toLowerCase() === dialogStatusFilter.toLowerCase()
                const q = dialogSearch.toLowerCase().trim()
                if (!q) return matchesStatus
                const summary = String(issue.summary || issue.title || "").toLowerCase()
                const key = String(issue.key || `TASK-${issue.id}`).toLowerCase()
                const assignee = String(getAssigneeDisplayName(issue, team)).toLowerCase()
                return matchesStatus && (summary.includes(q) || key.includes(q) || assignee.includes(q))
              })

              const statusList = ["ALL", "To Do", "In Progress", "In Review", "Done"]

              return (
                <Box>
                  <Stack 
                    direction={{ xs: "column", sm: "row" }} 
                    spacing={1.5} 
                    justifyContent="space-between" 
                    alignItems={{ xs: "stretch", sm: "center" }} 
                    mb={2}
                  >
                    <Box display="flex" flexWrap="wrap" gap={0.8}>
                      {statusList.map((st) => {
                        const count = st === "ALL"
                          ? (deepDiveData || []).length
                          : (deepDiveData || []).filter(i => getStatusDisplayName(i.status) === st).length
                        if (st !== "ALL" && count === 0 && dialogStatusFilter !== st) return null
                        const isSelected = dialogStatusFilter === st
                        const colorMap = {
                          "Done": "success",
                          "In Progress": "primary",
                          "In Review": "secondary",
                          "To Do": "warning"
                        }
                        return (
                          <Chip
                            key={st}
                            label={`${st} (${count})`}
                            size="small"
                            onClick={() => setDialogStatusFilter(st)}
                            color={isSelected ? (colorMap[st] || "primary") : "default"}
                            variant={isSelected ? "filled" : "outlined"}
                            sx={{
                              fontWeight: 700,
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              borderRadius: "6px"
                            }}
                          />
                        )
                      })}
                    </Box>

                    <TextField
                      placeholder="Search tasks..."
                      size="small"
                      value={dialogSearch}
                      onChange={(e) => setDialogSearch(e.target.value)}
                      sx={{ width: { xs: "100%", sm: 240 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: { height: 32, fontSize: "0.8rem", borderRadius: 1.5 }
                      }}
                    />
                  </Stack>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                        <TableRow>
                          <TableCell><strong>ASSIGNEE</strong></TableCell>
                          <TableCell><strong>TASK KEY & SUMMARY</strong></TableCell>
                          <TableCell><strong>DUE DATE</strong></TableCell>
                          <TableCell><strong>STATUS</strong></TableCell>
                          <TableCell><strong>RISK STATUS</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDialogIssues.length > 0 ? filteredDialogIssues.map((issue) => (
                          <TableRow 
                            key={issue.id} 
                            hover
                            onClick={() => handleOpenRiskDetails(issue)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={600}>{getAssigneeDisplayName(issue, team)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={600}>{issue.summary || issue.title}</Typography>
                              <Chip label={issue.key || `TASK-${issue.id}`} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(issue.dueDate || issue.endDate)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={getStatusDisplayName(issue.status)} size="small" color={getStatusColor(issue.status)} />
                            </TableCell>
                            <TableCell>
                              {issue.risk_status && issue.risk_status !== "None" ? (
                                <Chip 
                                  label={`${issue.risk_probability || 'Medium'} (${issue.risk_status})`} 
                                  size="small" 
                                  color={
                                    issue.risk_status === "Occurred" ? "error" :
                                    issue.risk_status === "Mitigating" ? "primary" : "warning"
                                  }
                                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                  No Active Risk
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              No tasks found matching current filter.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )
            })()}

            {deepDiveType === 'priority_severity' && (() => {
              const filteredIssues = (deepDiveData || []).filter((issue) => {
                const issuePriority = getPriorityDisplayName(issue.priority);
                const issueSeverity = getSeverityDisplayName(issue.severity);

                let matchesFilter = true;
                if (dialogStatusFilter === "ALL") {
                  matchesFilter = true;
                } else if (dialogStatusFilter === "Priority: High") {
                  matchesFilter = issuePriority === "High";
                } else if (dialogStatusFilter === "Priority: Medium") {
                  matchesFilter = issuePriority === "Medium";
                } else if (dialogStatusFilter === "Priority: Low") {
                  matchesFilter = issuePriority === "Low";
                } else if (dialogStatusFilter === "Severity: High") {
                  matchesFilter = issueSeverity === "High";
                } else if (dialogStatusFilter === "Severity: Medium") {
                  matchesFilter = issueSeverity === "Medium";
                } else if (dialogStatusFilter === "Severity: Low") {
                  matchesFilter = issueSeverity === "Low";
                } else if (dialogStatusFilter === "High") {
                  matchesFilter = issuePriority === "High" || issueSeverity === "High";
                } else if (dialogStatusFilter === "Medium") {
                  matchesFilter = issuePriority === "Medium" || issueSeverity === "Medium";
                } else if (dialogStatusFilter === "Low") {
                  matchesFilter = issuePriority === "Low" || issueSeverity === "Low";
                }

                const q = dialogSearch.toLowerCase().trim();
                if (!q) return matchesFilter;

                const summary = String(issue.summary || issue.title || "").toLowerCase();
                const key = String(issue.key || `TASK-${issue.id}`).toLowerCase();
                const assignee = String(getAssigneeDisplayName(issue, team)).toLowerCase();
                return matchesFilter && (summary.includes(q) || key.includes(q) || assignee.includes(q));
              });

              const filterOptions = [
                { label: "ALL", filterKey: "ALL", count: (deepDiveData || []).length, color: "default" },
                { label: "High Priority", filterKey: "Priority: High", count: (deepDiveData || []).filter(i => getPriorityDisplayName(i.priority) === "High").length, color: "primary" },
                { label: "Medium Priority", filterKey: "Priority: Medium", count: (deepDiveData || []).filter(i => getPriorityDisplayName(i.priority) === "Medium").length, color: "primary" },
                { label: "Low Priority", filterKey: "Priority: Low", count: (deepDiveData || []).filter(i => getPriorityDisplayName(i.priority) === "Low").length, color: "primary" },
                { label: "High Severity", filterKey: "Severity: High", count: (deepDiveData || []).filter(i => getSeverityDisplayName(i.severity) === "High").length, color: "error" },
                { label: "Medium Severity", filterKey: "Severity: Medium", count: (deepDiveData || []).filter(i => getSeverityDisplayName(i.severity) === "Medium").length, color: "error" },
                { label: "Low Severity", filterKey: "Severity: Low", count: (deepDiveData || []).filter(i => getSeverityDisplayName(i.severity) === "Low").length, color: "error" },
              ];

              return (
                <Box>
                  <Stack 
                    direction={{ xs: "column", sm: "row" }} 
                    spacing={1.5} 
                    justifyContent="space-between" 
                    alignItems={{ xs: "stretch", sm: "center" }} 
                    mb={2}
                  >
                    <Box display="flex" flexWrap="wrap" gap={0.8}>
                      {filterOptions.map((opt) => {
                        if (opt.filterKey !== "ALL" && opt.count === 0 && dialogStatusFilter !== opt.filterKey) return null;
                        const isSelected = dialogStatusFilter === opt.filterKey;
                        return (
                          <Chip
                            key={opt.filterKey}
                            label={`${opt.label} (${opt.count})`}
                            size="small"
                            onClick={() => setDialogStatusFilter(opt.filterKey)}
                            color={isSelected ? opt.color : "default"}
                            variant={isSelected ? "filled" : "outlined"}
                            sx={{
                              fontWeight: 700,
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              borderRadius: "6px"
                            }}
                          />
                        );
                      })}
                    </Box>

                    <TextField
                      placeholder="Search by key, summary, assignee..."
                      size="small"
                      value={dialogSearch}
                      onChange={(e) => setDialogSearch(e.target.value)}
                      sx={{ width: { xs: "100%", sm: 260 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: { height: 32, fontSize: "0.8rem", borderRadius: 1.5 }
                      }}
                    />
                  </Stack>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                        <TableRow>
                          <TableCell><strong>ISSUE KEY</strong></TableCell>
                          <TableCell><strong>SUMMARY</strong></TableCell>
                          <TableCell><strong>ASSIGNEE</strong></TableCell>
                          <TableCell><strong>PRIORITY</strong></TableCell>
                          <TableCell><strong>SEVERITY</strong></TableCell>
                          <TableCell><strong>STATUS</strong></TableCell>
                          <TableCell><strong>DUE DATE</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredIssues.length > 0 ? filteredIssues.map((issue) => {
                          const pDisplayName = getPriorityDisplayName(issue.priority);
                          const sDisplayName = getSeverityDisplayName(issue.severity);
                          return (
                            <TableRow 
                              key={issue.id} 
                              hover
                              onClick={() => handleOpenRiskDetails(issue)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Chip 
                                  label={issue.key || `TASK-${issue.id}`} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ fontWeight: 700, fontSize: "0.75rem" }} 
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#0F172A" }}>
                                  {issue.summary || issue.title}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {getAssigneeDisplayName(issue, team)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={pDisplayName} 
                                  size="small" 
                                  sx={getPriorityChipStyle(issue.priority)} 
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={sDisplayName} 
                                  size="small" 
                                  sx={getSeverityChipStyle(issue.severity)} 
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={getStatusDisplayName(issue.status)} 
                                  size="small" 
                                  color={getStatusColor(issue.status)} 
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(issue.dueDate || issue.endDate)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              No tasks found matching current filter.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })()}

            {deepDiveType === 'team' && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                    <TableRow>
                      <TableCell><strong>MEMBER NAME</strong></TableCell>
                      <TableCell><strong>ASSIGNED ROLE</strong></TableCell>
                      <TableCell><strong>ALLOCATION</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deepDiveData.length > 0 ? deepDiveData.map((member, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight={600}>{member.name || member.employee_id || 'Developer'}</Typography>
                          <Typography variant="caption" color="text.secondary">{member.email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={member.role || 'Developer'} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={`${member.allocation_percentage || member.allocation || 100}% Allocated`} size="small" color="success" />
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No team members assigned.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </Dialog>
        {/* Dedicated Risk Reason and Mitigation Dialog */}
        <Dialog
          open={riskDetailsOpen}
          onClose={() => setRiskDetailsOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "14px", overflow: "hidden" }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#FEF2F2', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning sx={{ color: "#EF4444" }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: "#991B1B", fontSize: "1.1rem" }}>
                Risk & Mitigation Profile: {riskDetailsTask?.key || 'TASK'}
              </Typography>
            </Box>
            <IconButton onClick={() => setRiskDetailsOpen(false)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {riskDetailsTask && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Task Summary & Metadata Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Work Item Summary
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.3, color: "#0F172A" }}>
                      {riskDetailsTask.summary || riskDetailsTask.title}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <Chip 
                      label={`Status: ${getStatusDisplayName(riskDetailsTask.status)}`} 
                      size="small" 
                      color={getStatusColor(riskDetailsTask.status)} 
                      sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, borderRadius: "6px" }} 
                    />
                    <Chip 
                      label={`Severity: ${riskDetailsTask.risk_probability || 'Medium'} (${riskDetailsTask.risk_status || 'Identified'})`} 
                      size="small" 
                      color={
                        riskDetailsTask.risk_status === "Occurred" ? "error" :
                        riskDetailsTask.risk_status === "Mitigating" ? "primary" : "warning"
                      }
                      sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, borderRadius: "6px" }}
                    />
                    <Chip 
                      label={`Impact: ${riskDetailsTask.risk_impact || "High"}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, borderRadius: "6px" }}
                    />
                    <Chip 
                      label={`Assignee: ${getAssigneeDisplayName(riskDetailsTask, team)}`} 
                      size="small" 
                      sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, borderRadius: "6px", bgcolor: "#EFF6FF", color: "#1D4ED8" }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* 3-Column Layout: Reason | Mitigation Plan | Action Items */}
                <Grid container spacing={2.5}>
                  {/* Column 1: Reason / Root Cause */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: "#fff7ed", p: 2, borderRadius: "10px", borderLeft: "4px solid #f97316", height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                        <Warning sx={{ fontSize: 16, color: "#ea580c" }} />
                        <Typography variant="caption" sx={{ color: "#9a3412", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Reason / Root Cause
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "#334155", fontSize: "0.85rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                        {riskDetailsTask.risk_reason || riskDetailsTask.description || "No detailed root cause reason was provided when raising this issue."}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Column 2: Mitigation Plan */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: "#f0fdf4", p: 2, borderRadius: "10px", borderLeft: "4px solid #16a34a", height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                        <CheckCircle sx={{ fontSize: 16, color: "#16a34a" }} />
                        <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Mitigation Plan
                        </Typography>
                      </Box>
                      {aiMitigationLoading ? (
                        <Box display="flex" alignItems="center" gap={1.2} py={2}>
                          <CircularProgress size={18} sx={{ color: "#16a34a" }} />
                          <Typography variant="body2" sx={{ color: "#15803d", fontStyle: "italic", fontSize: "0.82rem" }}>
                            Generating mitigation steps...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#166534", fontSize: "0.85rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                          {aiMitigationData?.mitigation || (aiMitigationData?.mitigationPlan && aiMitigationData.mitigationPlan[0]) || (riskDetailsTask.risk_mitigation && riskDetailsTask.risk_mitigation !== "No formal mitigation steps have been recorded yet." 
                            ? riskDetailsTask.risk_mitigation 
                            : `Refactor core module logic and execute comprehensive integration tests to resolve blocking impediments on ${riskDetailsTask.summary || 'deliverable'}.`)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Column 3: Action Items */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: "#eff6ff", p: 2, borderRadius: "10px", borderLeft: "4px solid #2563eb", height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                        <Assignment sx={{ fontSize: 16, color: "#2563eb" }} />
                        <Typography variant="caption" sx={{ color: "#1e40af", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Action Item
                        </Typography>
                      </Box>
                      {aiMitigationLoading ? (
                        <Box display="flex" alignItems="center" gap={1.2} py={2}>
                          <CircularProgress size={18} sx={{ color: "#2563eb" }} />
                          <Typography variant="body2" sx={{ color: "#1e40af", fontStyle: "italic", fontSize: "0.82rem" }}>
                            Generating action items & owner...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#1e3a8a", fontSize: "0.85rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                          {aiMitigationData?.action || (aiMitigationData?.actionItems && aiMitigationData.actionItems[0]?.action) || riskDetailsTask.risk_action || `Implement technical code fixes and validate integration stability with ${getAssigneeDisplayName(riskDetailsTask, team) || "task owner"}.`}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  )
}

export default ProfessionalProjectDashboard